import { Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useState, useMemo } from 'react';
import { queryEvents, queryStorageLocations } from '../schema';
import { db } from '../instant';
import { id } from '@instantdb/react';
import { useNavigate } from 'react-router-dom';
import { usePackingListsByEvent } from '../hooks/usePackingListsByEvent';
import { getPackingListStats } from '../utils/packingListStats';
import { rebuildDagAndLpForTrip } from '../utils/tripRebuildTrigger';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated?: (tripId: string) => void;
}

export default function CreateTripDialog({ open, onClose, onCreated }: Props) {
    const navigate = useNavigate();
    const { data: eventsData } = queryEvents();
    const events = useMemo(() => eventsData?.events || [], [eventsData]);
    const { data: storageLocationsData } = queryStorageLocations();
    const storageLocations = useMemo(
        () => [...(storageLocationsData?.storageLocations ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [storageLocationsData]
    );

    const { packingListItemsByEvent, eventsWithPackingLists } = usePackingListsByEvent(events);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [sourceStorageLocationId, setSourceStorageLocationId] = useState<string | null>(null);
    const [destStorageLocationId, setDestStorageLocationId] = useState<string | null>(null);
    const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
    const [isCreating, setIsCreating] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    const handleSubmit = async () => {
        setValidationError(null);
        const trimmedName = name.trim();
        if (!trimmedName) {
            setValidationError('Name is required');
            return;
        }
        if (!sourceStorageLocationId) {
            setValidationError('Source storage location (before events) is required');
            return;
        }
        if (!destStorageLocationId) {
            setValidationError('Destination storage location (after events) is required');
            return;
        }

        setIsCreating(true);
        try {
            const tripId = id();
            const now = new Date();
            const eventIds = Array.from(selectedEventIds);

            const txs: Parameters<typeof db.transact>[0] = [
                db.tx.trips[tripId].update({
                    name: trimmedName,
                    description: description.trim() || undefined,
                    createdAt: now,
                    notes: undefined,
                }),
                db.tx.trips[tripId].link({ sourceStorageLocation: sourceStorageLocationId }),
                db.tx.trips[tripId].link({ destStorageLocation: destStorageLocationId }),
            ];
            if (eventIds.length > 0) {
                txs.push(db.tx.trips[tripId].link({ events: eventIds }));
            }

            await db.transact(txs);

            onClose();
            onCreated?.(tripId);
            navigate(`/logistics/trip/${tripId}`);
            rebuildDagAndLpForTrip(tripId).catch(console.error);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to create trip';
            setValidationError(message);
        } finally {
            setIsCreating(false);
        }
    };

    const handleClose = () => {
        setName('');
        setDescription('');
        setSourceStorageLocationId(null);
        setDestStorageLocationId(null);
        setSelectedEventIds(new Set());
        setValidationError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create Trip</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Trip name"
                    fullWidth
                    required
                    value={name}
                    onChange={e => setName(e.target.value)}
                />
                <TextField
                    margin="dense"
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                />

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Source (before events)
                </Typography>
                <Autocomplete
                    options={storageLocations}
                    getOptionLabel={option => option.name ?? ''}
                    value={storageLocations.find(s => s.id === sourceStorageLocationId) ?? null}
                    onChange={(_e, v) => {
                        setSourceStorageLocationId(v?.id ?? null);
                        setValidationError(null);
                    }}
                    renderInput={params => <TextField {...params} placeholder="Select source storage location" size="small" required />}
                    sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Destination (after events)
                </Typography>
                <Autocomplete
                    options={storageLocations}
                    getOptionLabel={option => option.name ?? ''}
                    value={storageLocations.find(s => s.id === destStorageLocationId) ?? null}
                    onChange={(_e, v) => {
                        setDestStorageLocationId(v?.id ?? null);
                        setValidationError(null);
                    }}
                    renderInput={params => (
                        <TextField {...params} placeholder="Select destination storage location" size="small" required />
                    )}
                    sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Events in this trip
                </Typography>
                {eventsWithPackingLists.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No events with packing lists
                    </Typography>
                ) : (
                    <Autocomplete
                        multiple
                        options={eventsWithPackingLists}
                        getOptionLabel={option => String(option.name ?? '')}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={eventsWithPackingLists.filter(e => selectedEventIds.has(e.id))}
                        onChange={(_e, newValue) => {
                            setSelectedEventIds(new Set(newValue.map(e => e.id)));
                            setValidationError(null);
                        }}
                        renderInput={params => <TextField {...params} placeholder="Select events" size="small" />}
                        renderOption={(props, event) => {
                            const { itemCount, trailerNames } = getPackingListStats(packingListItemsByEvent, event.id);
                            const dateStr = event.startDate
                                ? new Date(event.startDate as unknown as string).toLocaleDateString()
                                : 'No date';
                            return (
                                <li {...props} key={event.id}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.5 }}>
                                        <Typography variant="body1">{event.name ?? ''}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {dateStr}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                mt: 0.5,
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                {itemCount} item{itemCount !== 1 ? 's' : ''}
                                            </Typography>
                                            {trailerNames.length > 0 && (
                                                <>
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        •
                                                    </Typography>
                                                    {trailerNames.map(name => (
                                                        <Chip key={name} label={name} size="small" variant="outlined" />
                                                    ))}
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </li>
                            );
                        }}
                    />
                )}

                {validationError && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {validationError}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isCreating}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isCreating}>
                    {isCreating ? 'Creating…' : 'Create Trip'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
